import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import ContentLoader, { IContentLoaderProps } from 'react-content-loader';
import { getScrollParent } from 'utils';
import useResizeObserver from 'hooks/useResizeObserver';

import './TableRowPlaceholder.styles.scss';

const HORIZONTAL_PADDING = 16;

const TableRowContentLoader = ({
  width,
  ...rest
}: IContentLoaderProps & { width: number | string }) => (
  <ContentLoader
    viewBox={`0 0 ${width} 50`}
    backgroundColor="#e7f1fc"
    foregroundColor="#ffffff"
    {...rest}
  >
    <rect x="0" y="0" rx="16" ry="16" height="50" width={width} />
  </ContentLoader>
);

type TableRowPlaceholderProps = {
  className?: string;
  numberOfColumns: number;
};

const TableRowPlaceholder: React.FC<TableRowPlaceholderProps> = (props) => {
  const { className, numberOfColumns } = props;

  const classes = classNames('table-row-placeholder', className);

  const [width, setWidth] = useState(0);
  const [target, setTarget] = useState<HTMLElement>(null);

  const ref = useRef<HTMLTableRowElement>(null);
  const divRef = useRef<HTMLDivElement>(null);

  const style = useMemo(
    () => (width ? { width, minWidth: width, maxWidth: width } : {}),
    [width],
  );

  const update = useCallback(() => {
    if (!target?.clientWidth) return;

    setWidth(target.clientWidth - 2 * HORIZONTAL_PADDING);
  }, [target]);

  const onScroll = useCallback(() => {
    if (!divRef?.current || !target?.scrollLeft) return;

    divRef.current.style.transform = `translateX(${target.scrollLeft}px)`;
  }, [target]);

  useEffect(() => {
    if (!target) return;

    onScroll();
    target.addEventListener('scroll', onScroll, { passive: true });

    return () => target.removeEventListener('scroll', onScroll);
  }, [target, onScroll]);

  useEffect(() => setTarget(getScrollParent(ref.current, true)), []);

  useResizeObserver(target, update);

  return (
    <tr className={classes} ref={ref}>
      <td colSpan={numberOfColumns}>
        <div ref={divRef} style={style}>
          <TableRowContentLoader width={width} />
        </div>
      </td>
    </tr>
  );
};

export default TableRowPlaceholder;
